/**
 * The metadata engine.
 *
 * CRA has no `generateMetadata`, so this module is the equivalent: a set of
 * pure functions that turn a real page data model into the exact head tags a
 * page should emit (title, description, canonical, robots, Open Graph,
 * Twitter). Both the React `<SeoHead>` component and the Node prerenderer call
 * these same functions, so what a crawler receives in raw HTML and what the SPA
 * renders can never drift.
 *
 * Hard rule: every templated sentence is built ONLY from values present in the
 * supplied model. Counts, price ranges and ratings are omitted entirely when
 * the marketplace has no real data for them — they are never invented,
 * rounded up, or filled with placeholders.
 *
 * CommonJS on purpose — shared by the CRA bundle and the Node build scripts.
 */

const {
  canonicalUrl,
  normalizePath,
  getSiteOrigin,
  normalizeEnvironment,
  ROUTES,
} = require('./urls');
const { resolveState } = require('./states');
const { resolveServiceCategory } = require('./taxonomy');
const { ROBOTS_INDEX, ROBOTS_NOINDEX } = require('./eligibility');

const SITE_NAME = 'LookBel';
const TITLE_SUFFIX = ` | ${SITE_NAME}`;
const MAX_TITLE_LENGTH = 60;
const MAX_DESCRIPTION_LENGTH = 155;
const DEFAULT_OG_IMAGE_PATH = '/lb-logo512.png';

/**
 * Truncate on a word boundary, never mid-word, never with a dangling hyphen.
 * @param {string} value
 * @param {number} max
 * @returns {string}
 */
function truncate(value, max) {
  const text = String(value == null ? '' : value).replace(/\s+/g, ' ').trim();
  if (text.length <= max) return text;
  const cut = text.slice(0, max - 1);
  const lastSpace = cut.lastIndexOf(' ');
  return `${(lastSpace > max * 0.6 ? cut.slice(0, lastSpace) : cut).replace(/[\s,;:.-]+$/, '')}…`;
}

/**
 * @param {string} core
 * @returns {string} `core | LookBel`, trimmed so the whole title stays scannable
 */
function withSuffix(core) {
  const base = String(core == null ? '' : core).replace(/\s+/g, ' ').trim();
  const room = MAX_TITLE_LENGTH - TITLE_SUFFIX.length;
  return `${truncate(base, room)}${TITLE_SUFFIX}`;
}

/**
 * @param {unknown} value
 * @returns {number} a usable positive integer, or 0
 */
function positiveInt(value) {
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? Math.floor(n) : 0;
}

/**
 * Real price range only. Returns null unless both ends are real positive
 * numbers — we never publish "$0" or a one-sided guess.
 *
 * @param {unknown} min
 * @param {unknown} max
 * @returns {string | null}
 */
function formatPriceRange(min, max) {
  const lo = Number(min);
  const hi = Number(max);
  if (!Number.isFinite(lo) || !Number.isFinite(hi) || lo <= 0 || hi <= 0) return null;
  const low = Math.round(Math.min(lo, hi));
  const high = Math.round(Math.max(lo, hi));
  return low === high ? `$${low}` : `$${low}–$${high}`;
}

/**
 * @param {unknown} average
 * @param {unknown} count
 * @returns {string | null} e.g. "4.9 from 27 reviews", or null when unrated
 */
function formatRating(average, count) {
  const avg = Number(average);
  const total = positiveInt(count);
  if (!Number.isFinite(avg) || avg <= 0 || total < 1) return null;
  return `${avg.toFixed(1)} from ${total} review${total === 1 ? '' : 's'}`;
}

/**
 * @param {string} city
 * @param {string} stateInput
 * @returns {string} "Austin, TX" — falls back gracefully if the state is unknown
 */
function formatPlace(city, stateInput) {
  const state = resolveState(stateInput);
  const cityName = String(city == null ? '' : city).trim();
  if (cityName && state) return `${cityName}, ${state.code}`;
  if (cityName) return cityName;
  return state ? state.name : '';
}

/** Join non-empty sentence fragments into one clean description. */
function sentences(parts) {
  return parts
    .filter((p) => typeof p === 'string' && p.trim().length > 0)
    .map((p) => p.trim().replace(/\.*$/, '.'))
    .join(' ');
}

/**
 * @typedef {Object} PageMetadata
 * @property {string}  title
 * @property {string}  description
 * @property {string}  canonicalPath
 * @property {string}  canonical      absolute URL
 * @property {string}  robots
 * @property {boolean} indexable
 * @property {string}  h1
 * @property {{ title: string, description: string, url: string, type: string, image: string, siteName: string }} openGraph
 * @property {{ card: string, title: string, description: string, image: string }} twitter
 */

/**
 * @param {Object} input
 * @returns {PageMetadata}
 */
function buildMetadata(input) {
  const {
    title,
    description,
    path,
    h1,
    indexable,
    environment,
    siteUrl,
    ogType,
    imagePath,
  } = input;

  const canonicalPath = normalizePath(path);
  const absoluteCanonical = canonicalUrl(canonicalPath, { environment, siteUrl });
  const origin = getSiteOrigin(environment, siteUrl);
  const image = /^https?:\/\//.test(String(imagePath || ''))
    ? String(imagePath)
    : `${origin}${imagePath || DEFAULT_OG_IMAGE_PATH}`;

  const finalTitle = title;
  const finalDescription = truncate(description, MAX_DESCRIPTION_LENGTH);

  // Hard environment gate. Even a page that clears every eligibility rule is
  // noindex outside production, so staging/QA/preview builds can never compete
  // with production for the same content. This is enforced here, in the single
  // function every page's metadata flows through, rather than per page.
  const isProduction = normalizeEnvironment(environment) === 'production';
  const isIndexable = Boolean(indexable) && isProduction;

  return {
    title: finalTitle,
    description: finalDescription,
    canonicalPath,
    canonical: absoluteCanonical,
    robots: isIndexable ? ROBOTS_INDEX : ROBOTS_NOINDEX,
    indexable: isIndexable,
    h1: h1 || finalTitle,
    openGraph: {
      title: finalTitle,
      description: finalDescription,
      url: absoluteCanonical,
      type: ogType || 'website',
      image,
      siteName: SITE_NAME,
    },
    twitter: {
      card: 'summary_large_image',
      title: finalTitle,
      description: finalDescription,
      image,
    },
  };
}

/* ------------------------------------------------------------------ *
 * Per-page-type builders. Each takes a real data model.
 * ------------------------------------------------------------------ */

/**
 * @param {{ environment?: string, siteUrl?: string }} [ctx]
 */
function homeMetadata(ctx) {
  const c = ctx || {};
  return buildMetadata({
    title: withSuffix('Book Beauty & Wellness Pros Near You'),
    description:
      'LookBel connects you with independent beauty and wellness professionals across the United States. Browse real profiles, compare services and book online.',
    path: ROUTES.home(),
    h1: 'Book beauty and wellness professionals near you',
    indexable: true,
    environment: c.environment,
    siteUrl: c.siteUrl,
  });
}

/**
 * @param {{ stylistCount?: number, cityCount?: number, stateCount?: number,
 *           environment?: string, siteUrl?: string }} model
 */
function stylistDirectoryMetadata(model) {
  const m = model || {};
  const stylistCount = positiveInt(m.stylistCount);
  const cityCount = positiveInt(m.cityCount);

  const supply = stylistCount
    ? `Browse ${stylistCount} verified professional${stylistCount === 1 ? '' : 's'}${
        cityCount ? ` across ${cityCount} cit${cityCount === 1 ? 'y' : 'ies'}` : ''
      }`
    : 'Browse verified beauty and wellness professionals';

  return buildMetadata({
    title: withSuffix('Browse Beauty & Wellness Professionals'),
    description: sentences([
      supply,
      'See real services, availability and pricing, then book directly on LookBel',
    ]),
    path: ROUTES.stylistDirectory(),
    h1: 'Browse beauty and wellness professionals',
    indexable: true,
    environment: m.environment,
    siteUrl: m.siteUrl,
  });
}

/**
 * @param {{ serviceCategory: string, stylistCount?: number, cityCount?: number,
 *           priceMin?: number, priceMax?: number, indexable?: boolean,
 *           environment?: string, siteUrl?: string }} model
 */
function serviceHubMetadata(model) {
  const m = model || {};
  const category = resolveServiceCategory(m.serviceCategory);
  if (!category) return null;

  const stylistCount = positiveInt(m.stylistCount);
  const cityCount = positiveInt(m.cityCount);
  const priceRange = formatPriceRange(m.priceMin, m.priceMax);

  return buildMetadata({
    title: withSuffix(`Book ${category.providerNoun} Near You`),
    description: sentences([
      stylistCount
        ? `Compare ${stylistCount} ${category.providerNoun.toLowerCase()} on LookBel${
            cityCount ? ` in ${cityCount} cit${cityCount === 1 ? 'y' : 'ies'}` : ''
          }`
        : `Find ${category.providerNoun.toLowerCase()} on LookBel`,
      priceRange ? `Typical ${category.serviceNoun} prices run ${priceRange}` : '',
      'View real profiles and book online',
    ]),
    path: ROUTES.service(category.slug),
    h1: `${category.providerNoun} on LookBel`,
    indexable: m.indexable !== false,
    environment: m.environment,
    siteUrl: m.siteUrl,
  });
}

/**
 * @param {{ state: string, stylistCount?: number, cityCount?: number,
 *           indexable?: boolean, environment?: string, siteUrl?: string }} model
 */
function stateMetadata(model) {
  const m = model || {};
  const state = resolveState(m.state);
  if (!state) return null;

  const stylistCount = positiveInt(m.stylistCount);
  const cityCount = positiveInt(m.cityCount);

  return buildMetadata({
    title: withSuffix(`Beauty & Wellness Pros in ${state.name}`),
    description: sentences([
      stylistCount
        ? `${stylistCount} independent beauty and wellness professional${
            stylistCount === 1 ? '' : 's'
          } serving ${state.name}${cityCount ? ` across ${cityCount} cit${cityCount === 1 ? 'y' : 'ies'}` : ''}`
        : `Find independent beauty and wellness professionals serving ${state.name}`,
      'Browse by city and book online with LookBel',
    ]),
    path: ROUTES.state(state.slug),
    h1: `Beauty and wellness professionals in ${state.name}`,
    indexable: m.indexable !== false,
    environment: m.environment,
    siteUrl: m.siteUrl,
  });
}

/**
 * @param {{ city: string, state: string, stylistCount?: number,
 *           serviceCount?: number, priceMin?: number, priceMax?: number,
 *           indexable?: boolean, environment?: string, siteUrl?: string }} model
 */
function cityMetadata(model) {
  const m = model || {};
  const state = resolveState(m.state);
  const city = String(m.city == null ? '' : m.city).trim();
  if (!state || !city) return null;

  const place = formatPlace(city, state.code);
  const stylistCount = positiveInt(m.stylistCount);
  const priceRange = formatPriceRange(m.priceMin, m.priceMax);

  return buildMetadata({
    title: withSuffix(`Beauty & Wellness Pros in ${place}`),
    description: sentences([
      stylistCount
        ? `Compare ${stylistCount} beauty and wellness professional${
            stylistCount === 1 ? '' : 's'
          } serving ${place}`
        : `Beauty and wellness professionals serving ${place}`,
      priceRange ? `Services from ${priceRange}` : '',
      'Book online with LookBel',
    ]),
    path: ROUTES.city(state.slug, city),
    h1: `Beauty and wellness professionals in ${place}`,
    indexable: m.indexable !== false,
    environment: m.environment,
    siteUrl: m.siteUrl,
  });
}

/**
 * @param {{ city: string, state: string, serviceCategory: string,
 *           stylistCount?: number, priceMin?: number, priceMax?: number,
 *           indexable?: boolean, environment?: string, siteUrl?: string }} model
 */
function cityServiceMetadata(model) {
  const m = model || {};
  const state = resolveState(m.state);
  const category = resolveServiceCategory(m.serviceCategory);
  const city = String(m.city == null ? '' : m.city).trim();
  if (!state || !category || !city) return null;

  const place = formatPlace(city, state.code);
  const stylistCount = positiveInt(m.stylistCount);
  const priceRange = formatPriceRange(m.priceMin, m.priceMax);

  return buildMetadata({
    title: withSuffix(`${category.providerNoun} in ${place}`),
    description: sentences([
      stylistCount
        ? `Compare ${stylistCount} ${category.providerNoun.toLowerCase()} in ${place}`
        : `Looking for ${category.providerNoun.toLowerCase()} in ${place}`,
      priceRange ? `${category.label} services from ${priceRange}` : '',
      'See real profiles and book online with LookBel',
    ]),
    path: ROUTES.cityService(state.slug, city, category.slug),
    h1: `${category.providerNoun} in ${place}`,
    indexable: m.indexable !== false,
    environment: m.environment,
    siteUrl: m.siteUrl,
  });
}

/**
 * Public stylist profile. Only public fields are ever referenced here —
 * never email, phone, street address or payment details.
 *
 * @param {{ seoSlug: string, displayName: string, city?: string, state?: string,
 *           primaryCategory?: string, shortBio?: string, ratingAverage?: number,
 *           ratingCount?: number, priceMin?: number, priceMax?: number,
 *           profilePhotoUrl?: string, indexable?: boolean,
 *           environment?: string, siteUrl?: string }} model
 */
function stylistProfileMetadata(model) {
  const m = model || {};
  const name = String(m.displayName == null ? '' : m.displayName).trim();
  if (!name || !m.seoSlug) return null;

  const category = resolveServiceCategory(m.primaryCategory);
  const place = formatPlace(m.city, m.state);
  const rating = formatRating(m.ratingAverage, m.ratingCount);
  const priceRange = formatPriceRange(m.priceMin, m.priceMax);

  // Singular provider noun for one person: "Braiders" -> "Braider".
  const role = category ? category.providerNoun.replace(/s$/, '') : '';
  const titleCore = [name, [role, place].filter(Boolean).join(' in ')]
    .filter(Boolean)
    .join(' — ');

  return buildMetadata({
    title: withSuffix(titleCore),
    description: sentences([
      m.shortBio ? String(m.shortBio) : [role, place && `serving ${place}`].filter(Boolean).join(' '),
      rating ? `Rated ${rating}` : '',
      priceRange ? `Services from ${priceRange}` : '',
      'Book on LookBel',
    ]),
    path: ROUTES.stylistProfile(m.seoSlug),
    h1: name,
    indexable: m.indexable !== false,
    ogType: 'profile',
    imagePath: m.profilePhotoUrl || undefined,
    environment: m.environment,
    siteUrl: m.siteUrl,
  });
}

module.exports = {
  SITE_NAME,
  MAX_TITLE_LENGTH,
  MAX_DESCRIPTION_LENGTH,
  DEFAULT_OG_IMAGE_PATH,
  truncate,
  withSuffix,
  formatPriceRange,
  formatRating,
  formatPlace,
  buildMetadata,
  homeMetadata,
  stylistDirectoryMetadata,
  serviceHubMetadata,
  stateMetadata,
  cityMetadata,
  cityServiceMetadata,
  stylistProfileMetadata,
};
